import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import {
  useGetBankAccountsQuery,
  useGetChequebookQuery,
  useGetChequebooksQuery,
  useGetChequebookLeavesAvailableQuery,
  useGetChequeQuery,
  useCreateChequeMutation,
  useUpdateChequeMutation,
} from '../../../store/api/financeApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

function ChequeForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = id === 'new' || id === undefined;
  const presetChequebookId = searchParams.get('chequebookId');
  const presetLeaf = searchParams.get('leaf');
  const [form, setForm] = useState({
    bankAccountId: '',
    chequebookId: '',
    chequeNumber: '',
    amount: '',
    chequeDate: new Date().toISOString().split('T')[0],
    payee: '',
    memo: '',
    status: 'issued',
    clearedDate: '',
  });

  const { data: cheque, isLoading } = useGetChequeQuery(id, { skip: isNew });
  const { data: presetBook } = useGetChequebookQuery(presetChequebookId || '', { skip: !presetChequebookId || !isNew });
  const { data: bankAccounts = [] } = useGetBankAccountsQuery();
  const { data: chequebooks = [] } = useGetChequebooksQuery(
    { bankAccountId: form.bankAccountId || undefined },
    { skip: !form.bankAccountId }
  );
  const { data: leavesData } = useGetChequebookLeavesAvailableQuery(form.chequebookId, {
    skip: !form.chequebookId || !isNew,
  });
  const availableLeaves = leavesData?.available ?? [];
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';

  const [createCheque] = useCreateChequeMutation();
  const [updateCheque] = useUpdateChequeMutation();

  useEffect(() => {
    if (cheque) {
      setForm({
        bankAccountId: cheque.bankAccountId || '',
        chequebookId: cheque.chequebookId || '',
        chequeNumber: cheque.chequeNumber || '',
        amount: cheque.amount != null ? String(cheque.amount) : '',
        chequeDate: cheque.chequeDate ? cheque.chequeDate.slice(0, 10) : '',
        payee: cheque.payee || '',
        memo: cheque.memo || '',
        status: cheque.status || 'issued',
        clearedDate: cheque.clearedDate ? cheque.clearedDate.slice(0, 10) : '',
      });
    }
  }, [cheque]);

  useEffect(() => {
    if (isNew && presetBook && presetChequebookId) {
      setForm((p) => ({
        ...p,
        bankAccountId: presetBook.bankAccountId || p.bankAccountId,
        chequebookId: presetChequebookId,
        chequeNumber: presetLeaf != null && presetLeaf !== '' ? String(presetLeaf) : p.chequeNumber,
      }));
    }
  }, [isNew, presetBook, presetChequebookId, presetLeaf]);

  const onBankAccountChange = (bankAccountId) => {
    setForm((p) => ({ ...p, bankAccountId, chequebookId: '', chequeNumber: '' }));
  };
  const onChequebookChange = (chequebookId) => {
    setForm((p) => ({ ...p, chequebookId, chequeNumber: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.bankAccountId || !form.chequeNumber || !form.amount || !form.chequeDate) {
      alert('Bank account, cheque number, amount and date are required.');
      return;
    }
    const amount = parseFloat(form.amount);
    if (isNaN(amount) || amount <= 0) {
      alert('Amount must be a positive number.');
      return;
    }
    try {
      if (isNew) {
        await createCheque({
          bankAccountId: form.bankAccountId,
          chequebookId: form.chequebookId || undefined,
          chequeNumber: form.chequeNumber.trim(),
          amount,
          chequeDate: form.chequeDate,
          payee: form.payee?.trim() || undefined,
          memo: form.memo?.trim() || undefined,
        }).unwrap();
        navigate('/finance/cheques');
      } else {
        await updateCheque({
          id,
          status: form.status,
          clearedDate: form.clearedDate || undefined,
          payee: form.payee?.trim() || undefined,
          memo: form.memo?.trim() || undefined,
        }).unwrap();
        navigate('/finance/cheques');
      }
    } catch (err) {
      alert(err?.data?.message || 'Error saving');
    }
  };

  if (!isNew && isLoading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/finance/cheques')}>
          Back
        </Button>
        {!isNew && id && (
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => navigate(`/finance/cheques/${id}/print`)}>
            Print
          </Button>
        )}
      </Box>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 3 }}>
        {isNew ? 'Add Cheque' : 'Edit Cheque'}
      </Typography>
      <Card sx={{ maxWidth: 560 }}>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              select
              label="Bank account"
              value={form.bankAccountId}
              onChange={(e) => onBankAccountChange(e.target.value)}
              required
              disabled={!isNew}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">Select</MenuItem>
              {bankAccounts.filter((b) => b.isActive).map((b) => (
                <MenuItem key={b.id} value={b.id}>{b.name} {b.accountNumber ? `(${b.accountNumber})` : ''}</MenuItem>
              ))}
            </TextField>
            {isNew && form.bankAccountId && (
              <TextField
                fullWidth
                select
                label="Chequebook (optional)"
                value={form.chequebookId}
                onChange={(e) => onChequebookChange(e.target.value)}
                sx={{ mb: 2 }}
              >
                <MenuItem value="">None – enter cheque number manually</MenuItem>
                {chequebooks.filter((b) => b.status === 'active').map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name} ({b.startLeaf}–{b.endLeaf})
                  </MenuItem>
                ))}
              </TextField>
            )}
            {form.chequebookId ? (
              <TextField
                fullWidth
                select
                label="Leaf (cheque number)"
                value={form.chequeNumber}
                onChange={(e) => setForm((p) => ({ ...p, chequeNumber: e.target.value }))}
                required
                disabled={!isNew}
                helperText={`Available leaves from chequebook (${availableLeaves.length} left)`}
                sx={{ mb: 2 }}
              >
                <MenuItem value="">Select leaf</MenuItem>
                {availableLeaves.map((leaf) => (
                  <MenuItem key={leaf} value={String(leaf)}>{leaf}</MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                fullWidth
                label="Cheque number"
                value={form.chequeNumber}
                onChange={(e) => setForm((p) => ({ ...p, chequeNumber: e.target.value }))}
                required
                disabled={!isNew}
                sx={{ mb: 2 }}
              />
            )}
            <TextField
              fullWidth
              type="number"
              inputProps={{ step: 0.01, min: 0 }}
              label="Amount"
              value={form.amount}
              onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
              required
              disabled={!isNew}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="date"
              label="Cheque date"
              value={form.chequeDate}
              onChange={(e) => setForm((p) => ({ ...p, chequeDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              required
              disabled={!isNew}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Payee"
              value={form.payee}
              onChange={(e) => setForm((p) => ({ ...p, payee: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Memo"
              value={form.memo}
              onChange={(e) => setForm((p) => ({ ...p, memo: e.target.value }))}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            {!isNew && (
              <>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="issued">Issued</MenuItem>
                  <MenuItem value="cleared">Cleared</MenuItem>
                  <MenuItem value="bounced">Bounced</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </TextField>
                {(form.status === 'cleared') && (
                  <TextField
                    fullWidth
                    type="date"
                    label="Cleared date"
                    value={form.clearedDate}
                    onChange={(e) => setForm((p) => ({ ...p, clearedDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 2 }}
                  />
                )}
              </>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button type="submit" variant="contained">
                {isNew ? 'Create' : 'Save'}
              </Button>
              <Button onClick={() => navigate('/finance/cheques')}>Cancel</Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default ChequeForm;
